import styles from './index.module.less';
import { getArticleDetail } from '@/api/sip/bookmap/ditaContent';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEventEmitter, useLockFn, useRequest, useUnmount } from 'ahooks';
import Empty from './Empty';
import { Button, message, Spin } from 'antd';
import { Tabs, Image } from 'antd-v5';
import { XModal } from 'xui-pro';
import { getAppRootDom } from '@/App';
import type { EventEmitter } from 'ahooks/lib/useEventEmitter';
import type { ManualRecord } from '@/api/sip/bookmap/list';
import { isTopicExistsUsingPost } from '@/api/sip/bookmap/refExists';
import { useParams } from 'react-router-dom';
import type { WorkItem } from './WiTag/type';
import { WITable } from './WiTag';
import { useTranslation } from '@/hooks/useTranslation';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';

type DetailProps = {
  title: string;
  manualRecord: ManualRecord;
  code?: string;
  uniqueCode?: string;
  version?: string;
};

type Props = DetailProps & {
  active: boolean;
  tabChange$?: EventEmitter<void>;
  resizeStatusChange$?: EventEmitter<boolean>;
};

type IframeContainerProps = Props & {
  openDetail: (title: string, code: string, version: string) => void;
};

const onMouseUp = e => {
  // construct a mouseup event…
  const event = new MouseEvent('mouseup', { view: e.view, bubbles: true, cancelable: true });
  // …and dispatch it
  document.dispatchEvent(event);
};

const IframeContainer = (props: IframeContainerProps) => {
  const $ = useTranslation();
  const region = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );

  const [srcContent, setSrcContent] = useState<string>();
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const { modelCode } = useParams<{ modelCode: string }>();

  const scrollPositionRef = useRef(0);
  const [opacity, setOpacity] = useState(1);
  const [iframeLoading, setIframeLoading] = useState<boolean>(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(-1); // 当前预览的图片索引
  // 提取 iframe 内部的图片
  const extractImagesFromIframe = useCallback(() => {
    if (iframeRef.current?.contentDocument) {
      const images = iframeRef.current.contentDocument.querySelectorAll<HTMLImageElement>('.image');
      const imgUrls = Array.from(images).map(img => img.src);
      setPreviewImages(imgUrls);

      images.forEach((img, index) => {
        img.style.cursor = 'pointer';
        img.onclick = () => {
          setCurrentPreviewIndex(index); // 设置当前预览的图片索引
        };
      });
    }
  }, []);

  const getContent = useCallback(
    (unescapedHtmlString: string, resWorkItems?: WorkItem[]) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(unescapedHtmlString, 'text/html');
      if (doc === document) {
        return;
      }
      const titleEle = doc.getElementById('ariaid-title1');
      if (titleEle && titleEle.parentNode) {
        const applyStylesToEle = (element, cusstyles) => {
          Object.entries(cusstyles).forEach(([property, value]) => {
            element.style[property] = value;
          });
        };
        const xdragonDitaTitleStyles = {
          color: '#171F26',
          fontSize: '24px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center'
        };
        const xdragonDitaWorkhourStyles = {
          color: '#0068FF',
          fontSize: '12px',
          fontWeight: '400',
          padding: '2px 10px',
          backgroundColor: 'rgba(0, 104, 255, 0.1)',
          borderRadius: '5px',
          marginLeft: '8px'
        };
        const title = titleEle.textContent;
        const titleTag = doc.createElement('h1');
        titleTag.innerText = title ?? '';
        applyStylesToEle(titleTag, xdragonDitaTitleStyles);
        titleEle.parentNode.replaceChild(titleTag, titleEle);
        if (resWorkItems && resWorkItems.length > 0) {
          const workATag = doc.createElement('a');
          workATag.href = 'workhour:true';
          workATag.innerText = $('工时');
          workATag.className = 'xdragon-dita-workhour';
          applyStylesToEle(workATag, xdragonDitaWorkhourStyles);
          titleTag.appendChild(workATag);
        }
      }
      return doc.documentElement.outerHTML;
    },
    [$]
  );

  props.tabChange$?.useSubscription(() => {
    if (iframeRef.current && props.active) {
      scrollPositionRef.current = iframeRef.current.contentWindow?.scrollY || 0;
    }
  });

  const restoreScrollPosition = useCallback(() => {
    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.contentWindow?.scrollTo(0, scrollPositionRef.current);
        setOpacity(1);
      }
    }, 0);
  }, []);

  useEffect(() => {
    if (props.active) {
      restoreScrollPosition();
    } else {
      setOpacity(0);
    }
  }, [props.active, restoreScrollPosition, setOpacity]);

  props.resizeStatusChange$?.useSubscription(start => {
    if (iframeRef.current) {
      if (start) {
        iframeRef.current.contentWindow?.document.addEventListener('mouseup', onMouseUp);
      } else {
        iframeRef.current.contentWindow?.document.removeEventListener('mouseup', onMouseUp);
      }
    }
  });

  const { loading, error } = useRequest(
    async () => {
      try {
        if (props.version && props.code) {
          setIframeLoading(true);
          const apiPromise = getArticleDetail({
            topicCode: props.code,
            topicVersion: props.version,
            bookmapCode: props.manualRecord.bookmapCode,
            bookmapVersion: props.manualRecord.bookmapVersion
          });
          const res = await apiPromise;
          if (res.code === 200) {
            const htmlTextMatch = res.data.content?.match(/<html[^>]*>[\s\S]*<\/html>/i);
            if (htmlTextMatch) {
              setSrcContent(getContent(htmlTextMatch[0], res.data.workItemList));
              setWorkItems(res.data.workItemList ?? []);
            } else if (res.data.content) {
              throw new Error($('无效的HTML文本'));
            } else {
              throw new Error('');
            }
          } else {
            throw new Error(res.msg);
          }
        } else {
          throw new Error($('无效的CODE'));
        }
      } catch (e: any) {
        if (e.message) message.error(e.message);
        throw e;
      }
    },
    { manual: false, refreshDeps: [props.code], onError: () => setIframeLoading(false) }
  );

  const [showWorkhour, setShowWorkhour] = useState(false);
  const openDetail = useLockFn(async (functionCode?: string, code?: string) => {
    const res = await isTopicExistsUsingPost({
      bookmapCode: props.manualRecord.bookmapCode,
      bookmapVersion: props.manualRecord.bookmapVersion,
      topicCode: code,
      functionCode: functionCode,
      modelCode: modelCode,
      region
    });
    if (res.code === 200) {
      if (res.data.exists)
        props.openDetail(res.data.topicName!, res.data.topicCode!, res.data.topicVersion!);
    } else {
      message.error(res.msg);
    }
  });
  const onClick = useCallback(
    async (e: any) => {
      const target = e.target as HTMLAnchorElement;
      if (target.tagName === 'A') {
        try {
          const url = target.href;
          let code;
          let functionCode;
          if (url.startsWith('topiccode:')) {
            code = url.replace('topiccode:', '');
          } else if (url.startsWith('functioncode:')) {
            functionCode = url.replace('functioncode:', '');
          }
          if (code || functionCode) {
            console.log('code:', code, 'functionCode:', functionCode);
            e.preventDefault();
            openDetail(functionCode, code);
          }
          if (url === 'workhour:true') {
            e.preventDefault();
            setShowWorkhour(true);
          }
        } catch (err: any) {
          message.error(err.message || $('打开失败'));
        }
      }
    },
    [openDetail, $]
  );
  const [onLoad, setOnLoad] = useState(false);
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.addEventListener('click', onClick);
    }
    return () => {
      if (iframeRef.current) {
        iframeRef.current.contentWindow?.removeEventListener('click', onClick);
      }
    };
  }, [onClick, onLoad]);

  const iframeCallbackRef = useCallback(
    (node: HTMLIFrameElement) => {
      if (node) {
        //@ts-ignore
        iframeRef.current = node;
        node.onload = () => {
          extractImagesFromIframe(); // 提取图片并添加点击事件
          setIframeLoading(false);
        };
        setOnLoad(true);
      }
    },
    [extractImagesFromIframe]
  );

  useUnmount(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.document.removeEventListener('mouseup', onMouseUp);
      iframeRef.current.contentWindow?.removeEventListener('click', onClick);
    }
  });

  if (error) {
    return <Empty />;
  }

  return (
    <Spin spinning={loading || iframeLoading} wrapperClassName={styles['iframe-container']}>
      <iframe
        className={styles['iframe']}
        style={{ opacity: opacity }}
        srcDoc={srcContent}
        ref={iframeCallbackRef}
      />
      <XModal
        title={$('工时项')}
        visible={showWorkhour}
        onCancel={() => setShowWorkhour(false)}
        size='xLarge'
        footer={null}
        centered={true}
      >
        <WITable dataSource={workItems} />
      </XModal>
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            getContainer: getAppRootDom,
            visible: currentPreviewIndex > -1,
            current: currentPreviewIndex,
            onVisibleChange: visible => {
              if (!visible) setCurrentPreviewIndex(-1);
            },
            onChange: (current: number) => {
              setCurrentPreviewIndex(current);
            }
          }}
        >
          {previewImages.map((src, index) => (
            <Image key={`${index}` + 1} src={src} />
          ))}
        </Image.PreviewGroup>
      </div>
    </Spin>
  );
};

function getKey(item: DetailProps) {
  return item.uniqueCode || item.code;
}

export default (props: Props & { active: boolean }) => {
  const $ = useTranslation();
  const [details, setDetails] = useState<DetailProps[]>([]);
  const [activeKey, setActiveKey] = useState<string>();
  const tabChange$ = useEventEmitter();

  const openDetail = useCallback(
    (title: string, code: string, version: string) => {
      const key = code;
      const findIndex = details.findIndex(item => getKey(item) === key);
      tabChange$.emit();
      setActiveKey(key);
      if (findIndex === -1) {
        setDetails(prevDetails => {
          return [...prevDetails, { manualRecord: props.manualRecord, title, code, version }];
        });
      }
    },
    [details, props.manualRecord, tabChange$]
  );

  const onChange = (newActiveKey: string) => {
    tabChange$.emit();
    setActiveKey(newActiveKey);
  };

  const onEdit = useCallback(
    (
      e: string | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
      action: 'add' | 'remove'
    ) => {
      const targetKey = typeof e === 'string' ? e : (e as any).key;
      if (action === 'remove') {
        const index = details.findIndex(item => getKey(item) === targetKey);
        if (index === -1) return;
        if (details.length > 1 && targetKey === activeKey) {
          if (index > 0) {
            setActiveKey(getKey(details[index - 1]));
          } else {
            setActiveKey(getKey(details[index + 1]));
          }
        }
        setDetails(prevDetails => {
          const newDetails = [...prevDetails];
          newDetails.splice(index, 1);
          return newDetails;
        });
      }
    },
    [activeKey, details]
  );

  const removeAllIconSlot = useMemo(() => {
    return {
      right: (
        <Button type='link' onClick={() => setDetails([])}>
          {$('关闭全部')}
        </Button>
      )
    };
  }, [$]);

  return (
    <>
      <IframeContainer {...props} openDetail={openDetail} />
      {details.length > 0 ? (
        <XModal
          className={styles['dita-dialog']}
          title={null}
          footer={null}
          size='xLarge'
          width={'calc(100vw - 150px)'}
          visible={true}
          getContainer={getAppRootDom}
          closable={false}
          maskClosable={false}
          centered={true}
        >
          <Tabs
            onEdit={onEdit}
            onChange={onChange}
            activeKey={activeKey}
            type='editable-card'
            hideAdd={true}
            tabBarExtraContent={removeAllIconSlot}
          >
            {details.map(item => (
              <Tabs.TabPane
                key={getKey(item)}
                tab={item.title}
                style={{ height: 'calc(100vh - 130px)' }}
              >
                <IframeContainer
                  {...item}
                  openDetail={openDetail}
                  active={activeKey === getKey(item)}
                  tabChange$={tabChange$}
                />
              </Tabs.TabPane>
            ))}
          </Tabs>
        </XModal>
      ) : (
        <></>
      )}
    </>
  );
};
